import { useState, useEffect } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { palette } from '../../styles/tokens';
import { QUOTE_STATUSES, LINE_TYPES, INPUT } from './config';
import { MoneyInput } from '../../components/common/MoneyInput';
import { PercentInput } from '../../components/common/PercentInput';
import { usePercentSum } from '../../hooks/usePercentSum';
import { formatMoney } from '../../lib/money';
import { extractCanonicalPercentages } from '../../lib/paymentSchedule';
import { cancelQuote } from '../../lib/quoteMutations';
import { lookupErrorCopy } from '../../lib/errorMap';
import type { ProjectQuote, QuoteLineItem } from '../../types';

interface Props {
  quotes: ProjectQuote[];
  lineItems: QuoteLineItem[];
  userId: string;
  onCreateQuote: () => Promise<string | null>;
  onAddLineItem: (quoteId: string, item: Partial<QuoteLineItem>) => Promise<boolean>;
  onRemoveLineItem: (itemId: string, quoteId: string) => Promise<boolean>;
  onUpdateLineItem: (itemId: string, data: Partial<QuoteLineItem>, quoteId: string) => Promise<boolean>;
  onSendQuote: (quoteId: string) => Promise<boolean>;
  onUpdateQuoteStatus: (quoteId: string, status: 'approved' | 'declined') => Promise<boolean>;
  onUpdatePaymentSchedule: (quoteId: string, deposit: number, progress: number, final: number) => Promise<boolean>;
  onFetchLineItems: (quoteId: string) => void;
}

function fmt$(n: number): string { return `$${n.toLocaleString('en-CA', { minimumFractionDigits: 2 })}`; }

export function QuoteSection({ quotes, lineItems, onCreateQuote, onAddLineItem, onRemoveLineItem, onSendQuote, onUpdateQuoteStatus, onUpdatePaymentSchedule, onFetchLineItems }: Props) {
  const quote = quotes[0] ?? null;
  const [showAdd, setShowAdd] = useState(false);
  const [lineType, setLineType] = useState<string>('labour');
  const [lineDesc, setLineDesc] = useState('');
  const [lineQty, setLineQty] = useState('1');
  const [lineUnit, setLineUnit] = useState('');
  const [linePrice, setLinePrice] = useState('');
  const [depPct, setDepPct] = useState(25);
  const [progPct, setProgPct] = useState(50);
  const [finalPct, setFinalPct] = useState(25);
  const [creating, setCreating] = useState(false);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (quote) {
      onFetchLineItems(quote.id);
      const pcts = extractCanonicalPercentages(quote.payment_schedule_items ?? []);
      setDepPct(pcts.depositPercentage);
      setProgPct(pcts.progressPercentage);
      setFinalPct(pcts.finalPercentage);
    }
  }, [quote?.id]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const pctSum = usePercentSum([depPct, progPct, finalPct]);

  if (!quote) {
    return (
      <div style={{ textAlign: 'center', padding: '20px 0' }}>
        <div style={{ fontSize: 13, color: palette.g400, marginBottom: 10 }}>No quote yet.</div>
        <button onClick={async () => { setCreating(true); await onCreateQuote(); setCreating(false); }} disabled={creating}
          style={{ padding: '8px 20px', borderRadius: 8, border: 'none', background: palette.accent, color: palette.white, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
          {creating ? 'Creating...' : 'Create Quote'}
        </button>
      </div>
    );
  }

  const qs = QUOTE_STATUSES[quote.status] ?? QUOTE_STATUSES.draft;

  const handleAddLine = async () => {
    if (!lineDesc.trim()) return;
    // Q1.5 boundary: onAddLineItem accepts unit_price as number. linePrice
    // is canonical ("150.00") after MoneyInput's blur. Float round-trip is
    // precision-safe for a two-decimal canonical string.
    await onAddLineItem(quote.id, { line_type: lineType as QuoteLineItem['line_type'], description: lineDesc.trim(), quantity: Number(lineQty) || 1, unit: lineUnit || null, unit_price: Number(linePrice) || 0, sort_order: lineItems.length + 1 });
    setLineDesc(''); setLineQty('1'); setLineUnit(''); setLinePrice(''); setShowAdd(false);
  };

  const savePctSchedule = () => {
    if (!pctSum.isValid) return;
    void onUpdatePaymentSchedule(quote.id, depPct, progPct, finalPct);
  };

  const totalCopy = pctSum.isValid
    ? `Total: ${pctSum.total}%`
    : pctSum.diff > 0
      ? `Total: ${pctSum.total}% (${pctSum.diff}% remaining)`
      : `Total: ${pctSum.total}% (${Math.abs(pctSum.diff)}% over)`;

  return (
    <div>
      {/* Status */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
        <span style={{ padding: '3px 10px', borderRadius: 4, fontSize: 12, fontWeight: 600, background: qs.bg, color: qs.fg }}>{qs.label}</span>
        {quote.sent_at && <span style={{ fontSize: 11, color: palette.g400 }}>Sent {quote.sent_at.split('T')[0]}</span>}
        <div style={{ flex: 1 }} />
        {quote.status === 'draft' && (
          <button onClick={() => onSendQuote(quote.id)} style={{ padding: '5px 14px', borderRadius: 6, border: 'none', background: palette.blue, color: palette.white, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>Send Quote</button>
        )}
        {quote.status === 'sent' && (
          <>
            <button onClick={() => onUpdateQuoteStatus(quote.id, 'approved')} style={{ padding: '5px 14px', borderRadius: 6, border: 'none', background: palette.green, color: palette.white, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>Mark Approved</button>
            <button onClick={() => onUpdateQuoteStatus(quote.id, 'declined')} style={{ padding: '5px 14px', borderRadius: 6, border: 'none', background: palette.red, color: palette.white, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>Mark Declined</button>
          </>
        )}
        {(quote.status === 'sent' || quote.status === 'viewed' || quote.status === 'changes_requested') && (
          <button
            onClick={async () => {
              if (!window.confirm("Cancel this quote? The client will be notified it's no longer valid. This can't be undone.")) return;
              const result = await cancelQuote({ quoteId: quote.id, reason: null });
              if (!result.success) {
                window.alert(lookupErrorCopy('cancel_quote', result.error.code));
                return;
              }
              window.location.reload();
            }}
            style={{ padding: '5px 14px', borderRadius: 6, border: `1px solid ${palette.red}`, background: 'transparent', color: palette.red, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}
          >Cancel</button>
        )}
        {quote.status === 'cancelled' && (
          <span style={{ padding: '3px 10px', borderRadius: 4, fontSize: 11, fontWeight: 600, background: '#FEF2F2', color: '#991B1B' }}>Cancelled</span>
        )}
      </div>

      {/* Line items table */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: palette.g600 }}>Line Items</span>
          <button onClick={() => setShowAdd(!showAdd)} style={{ display: 'flex', alignItems: 'center', gap: 3, padding: '4px 10px', borderRadius: 4, border: 'none', background: palette.accent, color: palette.white, fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}><Plus size={12} /> Add</button>
        </div>

        {showAdd && (
          <div style={{ display: 'flex', gap: 6, marginBottom: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <select style={{ ...INPUT, width: 100 }} value={lineType} onChange={e => setLineType(e.target.value)}>
              {Object.entries(LINE_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <input style={{ ...INPUT, flex: 1, minWidth: 120 }} value={lineDesc} onChange={e => setLineDesc(e.target.value)} placeholder="Description" />
            <input type="number" style={{ ...INPUT, width: 50 }} value={lineQty} onChange={e => setLineQty(e.target.value)} placeholder="Qty" />
            <input style={{ ...INPUT, width: 60 }} value={lineUnit} onChange={e => setLineUnit(e.target.value)} placeholder="Unit" />
            <MoneyInput value={linePrice} onChange={setLinePrice} style={{ width: 100 }} />
            <button onClick={handleAddLine} disabled={!lineDesc.trim()} style={{ padding: '9px 12px', borderRadius: 8, border: 'none', background: palette.green, color: palette.white, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>Add</button>
          </div>
        )}

        {lineItems.length === 0 ? (
          <div style={{ fontSize: 12, color: palette.g400, padding: '8px 0' }}>No line items yet.</div>
        ) : (
          <div>
            {lineItems.map(item => (
              <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: `1px solid ${palette.g100}` }}>
                <span style={{ padding: '1px 6px', borderRadius: 3, fontSize: 10, fontWeight: 600, background: palette.g100, color: palette.g500, textTransform: 'capitalize', whiteSpace: 'nowrap' }}>{LINE_TYPES[item.line_type] ?? item.line_type}</span>
                <span style={{ flex: 1, fontSize: 13, color: palette.g700 }}>{item.description}</span>
                <span style={{ fontSize: 12, color: palette.g500, whiteSpace: 'nowrap' }}>{item.quantity} {item.unit ?? ''} x {formatMoney(item.unit_price)}</span>
                <span style={{ fontSize: 13, fontWeight: 600, color: palette.g800, width: 80, textAlign: 'right' }}>{fmt$(item.subtotal)}</span>
                <button onClick={() => onRemoveLineItem(item.id, quote.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: palette.g400, padding: 2 }}><Trash2 size={13} /></button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Totals */}
      <div style={{ borderTop: `1px solid ${palette.g150}`, paddingTop: 12, marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 24, fontSize: 13 }}>
          <span style={{ color: palette.g500 }}>Subtotal</span><span style={{ fontWeight: 600, color: palette.g800 }}>{fmt$(quote.subtotal)}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 24, fontSize: 13, marginTop: 4 }}>
          <span style={{ color: palette.g500 }}>{quote.tax_rate}% HST</span><span style={{ fontWeight: 600, color: palette.g800 }}>{fmt$(quote.tax_amount)}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 24, fontSize: 15, marginTop: 8 }}>
          <span style={{ fontWeight: 700, color: palette.g800 }}>Total</span><span style={{ fontWeight: 700, color: palette.g800 }}>{fmt$(quote.total)}</span>
        </div>
      </div>

      {/* Payment schedule */}
      <div style={{ borderTop: `1px solid ${palette.g150}`, paddingTop: 12 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: palette.g600, marginBottom: 8 }}>Payment Schedule</div>
        <div style={{ display: 'flex', gap: 12, fontSize: 12, alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            Deposit
            <PercentInput value={depPct} onChange={setDepPct} ariaLabel="Deposit percentage" style={{ width: 70 }} />
            = {fmt$(quote.total * depPct / 100)}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            Progress
            <PercentInput value={progPct} onChange={setProgPct} ariaLabel="Progress percentage" style={{ width: 70 }} />
            = {fmt$(quote.total * progPct / 100)}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            Final
            <PercentInput value={finalPct} onChange={setFinalPct} ariaLabel="Final percentage" style={{ width: 70 }} />
            = {fmt$(quote.total * finalPct / 100)}
          </div>
          <button onClick={savePctSchedule} disabled={!pctSum.isValid} title={!pctSum.isValid ? 'Payment schedule must total 100%' : undefined} style={{ padding: '4px 10px', borderRadius: 4, border: 'none', background: pctSum.isValid ? palette.accent : palette.g300, color: palette.white, fontSize: 11, fontWeight: 600, cursor: pctSum.isValid ? 'pointer' : 'not-allowed', fontFamily: 'inherit' }}>Save</button>
        </div>
        <div style={{
          fontSize: 11,
          color: pctSum.isValid ? palette.green : palette.red,
          marginTop: 6,
          fontWeight: 500,
        }}>
          {totalCopy}
        </div>
      </div>
    </div>
  );
}
